import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { axisRight, ContainerElement } from 'd3';
import { extent } from 'd3-array';
import { Axis, axisBottom, axisLeft } from 'd3-axis';
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { event as d3Event, select, Selection } from 'd3-selection';
import { line } from 'd3-shape';

import { AxisData } from './axis-data';
import { AxisDataAxisPathPoint } from './axis-data-axis-path-point';
import { Rect } from './util';

export interface AxisOptions {
    rect?: Rect;
    showYAxisLabel2?: boolean;
    isInsideDialog?: boolean;
    xDomain?: (domain: [number, number]) => [number, number];
    yDomain?: (domain: [number, number]) => [number, number];
    strokeColor?: (path: AxisDataAxisPathPoint[], index: number) => string;
    yAxisLabel?: (path: AxisDataAxisPathPoint[], index: number) => string;
}

export interface AxisPointMouseEvent {
    d: AxisDataAxisPathPoint;
    i: number;
    pointRect: ClientRect;
    event: MouseEvent;
    el: Element;
}

interface YAxisContextIn {
    xScale: ScaleLinear<number, number>;
    yScale: ScaleLinear<number, number>;
    yDomain: [number, number];
    xAxisHeight: number;
}

interface YAxisContextOut {
    yScale?: ScaleLinear<number, number>;
    yAxisWidth?: number;
    yAxisWidthRight?: number;
}

interface XAxisContextIn {
    preDraw: boolean;
    xDomain: [number, number];
    xScale: ScaleLinear<number, number>;
    yAxisWidth: number;
    yAxisWidthRight: number;
    xAxis: Axis<any>;
    xAxisHeight: number;
}

interface XAxisContextOut {
    xScale?: ScaleLinear<number, number>;
    xAxisHeight?: number;
}

interface GridContextIn {
    xScale: ScaleLinear<number, number>;
    xAxisHeight: number;
    yScale: ScaleLinear<number, number>;
    yAxisWidthRight: number;
    yAxisWidth: number;
}

interface DataContextIn {
    // dates: any[];
    xScale: ScaleLinear<number, number>;
    yDomain: [number, number];
    yScale: ScaleLinear<number, number>;
}

interface Context {
    xScale?: ScaleLinear<number, number>;
    xAxis?: Axis<any>;
    xAxisHeight?: number;
    yScale?: ScaleLinear<number, number>;
    yAxisWidth?: number;
    yAxisWidthRight?: number;
}

@Component({
    selector: 'ng-chart-chart, [ngChartChart]',
    template: '',
})
export class ChartComponent implements OnChanges, OnDestroy {
    @Input() options: AxisOptions;
    @Input() data: AxisData;

    @Output() pointMouseOver: EventEmitter<AxisPointMouseEvent>;
    @Output() pointMouseOut: EventEmitter<AxisPointMouseEvent>;

    private _svg: Selection<any, any, any, any>;
    private _root: Selection<any, any, any, any>;
    private _xAxisG: Selection<any, any, any, any>;
    private _yAxisG: Selection<SVGGElement, any, any, any>;
    private _xGridG: Selection<any, any, any, any>;
    private _yGridG: Selection<any, any, any, any>;
    private _dataG: Selection<any, any, any, any>;
    private _circlesG: Selection<any, any, any, any>;

    private _rectPadded: Rect;
    private _duration: number;
    private _ctx: Context;

    constructor(private readonly _el: ElementRef<HTMLElement>) {
        this.pointMouseOver = new EventEmitter();
        this.pointMouseOut = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('options') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('data')
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._render(true);
        }
    }

    ngOnDestroy(): void {
        this.pointMouseOver.complete();
        this.pointMouseOut.complete();
    }

    private _reset(): void {
        this._duration = 0;
        this._rectPadded = null;
        this._root = null;
        this._svg = null;
        this._el.nativeElement.textContent = '';
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _render(force?: boolean): Promise<void> {
        if ((this.data?.paths?.length ?? 0) === 0) {
            this._reset();
            return;
        }

        const firstRender = !this._svg;

        if (!this._svg) {
            this._svg = select(this._el.nativeElement)
                .append('svg')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('version', '1.1')
                .attr('tabindex', '-1');

            this._root = this._svg.append('g');

            this._xAxisG = this._root.append('g').attr('xAxisG', '');

            this._yAxisG = this._root.append('g').attr('yAxisG', '');

            this._xGridG = this._root.append('g').attr('xGridG', '');

            this._yGridG = this._root.append('g').attr('yGridG', '');

            this._dataG = this._root.append('g').attr('dataG', '');

            const pointsG = this._root
                .append('g')
                .attr('pointsG', '')
                .style('clip-path', 'url(#clip)');
            this._circlesG = pointsG.append('g').attr('circlesG', '');
        }

        const rect: Rect = {
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };
        rect.width = this.options.rect.width || rect.width;
        rect.height = this.options.rect.height || rect.height;
        rect.top = this.options.rect.top || rect.top;
        rect.right = this.options.rect.right || rect.right;
        rect.bottom = this.options.rect.bottom || rect.bottom;
        rect.left = this.options.rect.left || rect.left;

        this._svg.attr('width', rect.width).attr('height', rect.height);

        const clientRect = (
            this._svg.node() as Element
        ).getBoundingClientRect();
        rect.width = clientRect.width;
        rect.height = clientRect.height;

        const prevRectPadded = this._rectPadded;
        this._rectPadded = {
            width: rect.width - rect.right - rect.left,
            height: rect.height - rect.top - rect.bottom,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };

        this._duration =
            !firstRender && !this.options.isInsideDialog
                ? 5000 /* this.options.transitionDuration */
                : 0;

        /// ///////////////////////////

        this._root.interrupt();
        this._root.selectAll('*').interrupt();

        this._root
            .transition()
            .duration(this._duration)
            .attr('transform', `translate(${rect.left}, ${rect.top})`);

        /// ///////////////////////////

        return this._draw(force, prevRectPadded);
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _draw(force: boolean, prevRectPadded: Rect): Promise<any> {
        if (
            !force &&
            prevRectPadded.width === this._rectPadded.width &&
            prevRectPadded.height === this._rectPadded.height
        ) {
            return;
        }

        /// ///////////////////////////

        // single x-axis flat
        const xNums = this.data.paths.reduce((p, c) => {
            const d = c.points.map((e) => e.x);
            p.push(...d);
            return p;
        }, []);

        // single y-axis flat
        const yNums = this.data.paths.reduce((p, c) => {
            const d = c.points.map((e) => e.y);
            p.push(...d);
            return p;
        }, []);

        /// ///////////////////////////

        let xDomain = extent<number>(xNums);
        if (this.options.xDomain) {
            xDomain = this.options.xDomain(xDomain);
        }

        /// ///////////////////////////

        let yDomain = extent<number>(yNums);
        if (this.options.yDomain) {
            yDomain = this.options.yDomain(yDomain);
        }

        /// ///////////////////////////

        this._ctx = this._ctx || {};

        return (
            this._drawXAxis({
                preDraw: true,
                xDomain,
                xScale: null,
                yAxisWidth: null,
                yAxisWidthRight: null,
                xAxis: null,
                xAxisHeight: null,
            })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then((result) => {
                    this._ctx = { ...this._ctx, ...result };
                    return this._drawYAxis({
                        xScale: this._ctx.xScale,
                        yScale: this._ctx.yScale,
                        yDomain,
                        xAxisHeight: this._ctx.xAxisHeight,
                    });
                })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then((result) => {
                    this._ctx = { ...this._ctx, ...result };
                    return this._drawXAxis({
                        preDraw: false,
                        xDomain,
                        xScale: this._ctx.xScale,
                        yAxisWidth: this._ctx.yAxisWidth,
                        yAxisWidthRight: this._ctx.yAxisWidthRight,
                        xAxis: this._ctx.xAxis,
                        xAxisHeight: this._ctx.xAxisHeight,
                    });
                })
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    return this._drawGrid({
                        xScale: this._ctx.xScale,
                        xAxisHeight: this._ctx.xAxisHeight,
                        yScale: this._ctx.yScale,
                        yAxisWidthRight: this._ctx.yAxisWidthRight,
                        yAxisWidth: this._ctx.yAxisWidth,
                    });
                })
                .then(() => {
                    this._drawData({
                        // dates,
                        xScale: this._ctx.xScale,
                        yDomain,
                        yScale: this._ctx.yScale,
                    });
                })
        );
    }

    /** draw the y axes first to determine the true width of the x axis */
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawYAxis(ctx: YAxisContextIn): Promise<YAxisContextOut> {
        return new Promise<YAxisContextOut>((resolve) => {
            let yScale: ScaleLinear<number, number>;

            const writeLabel = (
                rootG: Selection<SVGGElement, any, any, any>,
                axisG: Selection<SVGGElement, any, any, any>,
            ): void => {
                const g = rootG.append('g').attr('text-anchor', 'middle');

                const t = g
                    .append('text')
                    .attr('dy', '0.32em')
                    .attr('x', 0)
                    .attr('y', 0)
                    .attr('fill', '#000')
                    .text(this.data.outcomeText);

                const aRect = axisG.node().getBoundingClientRect();
                const tRect = t.node().getBoundingClientRect();
                g.style(
                    'transform',
                    `translate(${-(aRect.width + tRect.height)}px, ${yScale(
                        (ctx.yDomain[1] - ctx.yDomain[0]) / 2,
                    )}px) rotate(-90deg)`,
                );
            };

            const createLeftScale = (): void => {
                yScale = scaleLinear()
                    .domain(ctx.yDomain)
                    .rangeRound([this._rectPadded.height - ctx.xAxisHeight, 0])
                    .nice();

                const rootG = this._yAxisG.append('g').attr('yAxisLeft', '');
                const g = rootG.append('g');
                g.call(axisLeft(yScale));

                writeLabel(rootG, g);

                rootG.attr(
                    'transform',
                    `translate(${
                        rootG.node().getBoundingClientRect().width
                    }, 0)`,
                );
            };

            const createRightScale = (): void => {
                const yScale2 = scaleLinear()
                    .domain(ctx.yDomain)
                    .rangeRound([this._rectPadded.height - ctx.xAxisHeight, 0])
                    .nice();

                const g = this._yAxisG.append('g').attr('yAxisRight', '');
                g.call(axisRight(yScale2));

                g.attr(
                    'transform',
                    `translate(${
                        this._rectPadded.width -
                        g.node().getBoundingClientRect().width
                    }, 0)`,
                );
            };

            this._yAxisG.selectAll('g').remove();

            createLeftScale();
            createRightScale();

            const yAxisWidth = this._yAxisG
                .select<SVGGElement>('[yAxisLeft]')
                .node()
                .getBoundingClientRect().width;
            const yAxisWidthRight =
                this._rectPadded.width -
                this._yAxisG
                    .select<SVGGElement>('[yAxisRight]')
                    .node()
                    .getBoundingClientRect().width;

            resolve?.({ yScale, yAxisWidth, yAxisWidthRight });
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawXAxis(ctx: XAxisContextIn): Promise<XAxisContextOut> {
        return new Promise<XAxisContextOut>((resolve) => {
            let xScale = ctx.xScale;
            if (!ctx.preDraw) {
                xScale.rangeRound([ctx.yAxisWidth, ctx.yAxisWidthRight]);
            } else {
                xScale = scaleLinear().domain(ctx.xDomain).nice();
            }

            const writeLabel = (
                rootG: Selection<SVGGElement, any, any, any>,
                axisG: Selection<SVGGElement, any, any, any>,
            ): void => {
                const g = rootG.append('g').attr('text-anchor', 'middle');

                const t = g
                    .append('text')
                    .attr('dy', '0.32em')
                    .attr('x', 0)
                    .attr('y', 0)
                    .attr('fill', '#000')
                    .text(this.data.behaviourText);

                const aRect = axisG.node().getBoundingClientRect();
                const tRect = t.node().getBoundingClientRect();
                g.attr(
                    'transform',
                    `translate(${xScale(
                        (ctx.xDomain[1] - ctx.xDomain[0]) / 2,
                    )}, ${aRect.height + tRect.height})`,
                );
            };

            let xAxisHeight: number;
            if (!ctx.preDraw) {
                this._xAxisG.selectAll('g').remove();

                const g = this._xAxisG.append('g');
                g.call(axisBottom(xScale));

                writeLabel(this._xAxisG, g);

                this._xAxisG.attr(
                    'transform',
                    `translate(0, ${
                        this._rectPadded.height - ctx.xAxisHeight
                    })`,
                );
            } else {
                const tempG = this._root.append('g');

                const g = tempG.attr('opacity', 0);
                g.call(axisBottom(xScale));

                writeLabel(tempG, g);

                xAxisHeight = tempG.node().getBoundingClientRect().height;
                tempG.remove();
            }

            resolve?.({ xScale, xAxisHeight });
        });
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _drawGrid(ctx: GridContextIn): Promise<void> {
        return new Promise<void>((resolve) => {
            const xGrid = axisBottom<number>(ctx.xScale)
                .tickSize(ctx.xAxisHeight)
                .tickFormat(() => '');

            const tickXSize = this._rectPadded.height - ctx.xAxisHeight;
            this._xGridG
                .attr('stroke', 'black')
                .attr('stroke-opacity', '0.5')
                .attr('stroke-dasharray', '2, 2')
                .call(xGrid as any)
                .each(function () {
                    const g = select<SVGGElement, any>(this);
                    g.selectAll('line').attr('y2', tickXSize);
                    g.selectAll('.domain').remove();
                });

            const yGrid = axisLeft(ctx.yScale)
                .tickSize(-ctx.yAxisWidthRight + ctx.yAxisWidth)
                .tickFormat(() => '');

            this._yGridG
                .attr('stroke', 'black')
                .attr('stroke-opacity', '0.5')
                .attr('stroke-dasharray', '2, 2')
                .call(yGrid as any)
                .attr('transform', `translate(${ctx.yAxisWidth}, 0)`)
                .each(function () {
                    const g = select<SVGGElement, any>(this);
                    g.selectAll('.domain').remove();
                });

            resolve?.();
        });
    }

    private _drawData(ctx: DataContextIn): void {
        this._root
            .selectAll('[dataG] > *, [circlesG] > *, [textsG] > *')
            .transition()
            .duration(this._duration * 0.5)
            .attr('fill-opacity', 0)
            .attr('stroke-opacity', 0)
            .remove();
        this.data.paths.forEach((path) => {
            if (!path.points.every((e) => e.y == null)) {
                path.points.forEach((dd, ii) => {
                    const circle = this._circlesG
                        .append('circle')
                        .attr('axis-id', 'default')
                        .attr(
                            'transform',
                            `translate(${ctx.xScale(dd.x)}, ${ctx.yScale(
                                dd.y,
                            )})`,
                        )
                        // .style('pointer-events', 'none')
                        .attr('fill-opacity', 0);
                    circle
                        .transition()
                        .delay(this._duration * 0.75)
                        .duration(this._duration * 0.25)
                        .attr('fill-opacity', 1)
                        .attr('fill', dd.color)
                        .attr('r', 8);

                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const that = this;
                    const pointMouseEvent = (
                        d: AxisDataAxisPathPoint,
                        i: number,
                        el: ContainerElement,
                    ): AxisPointMouseEvent => {
                        return {
                            d,
                            i, // TODO revise. -1 to offset the first element in tickDataPadded
                            pointRect: el.getBoundingClientRect(),
                            event: d3Event as MouseEvent,
                            el,
                        };
                    };

                    circle
                        .on('mouseover', function () {
                            circle
                                // .transition()
                                // .duration(250)
                                .attr('r', 10);
                            that.pointMouseOver.emit(
                                pointMouseEvent(
                                    dd,
                                    ii,
                                    this as ContainerElement,
                                ),
                            );
                        })
                        .on('mouseout', function () {
                            circle
                                // .transition()
                                // .duration(250)
                                .attr('r', 8);
                            that.pointMouseOut.emit(
                                pointMouseEvent(
                                    dd,
                                    ii,
                                    this as ContainerElement,
                                ),
                            );
                        });
                });
            }
        });

        const xyLine = line<AxisDataAxisPathPoint>()
            .x((d) => ctx.xScale(d.x))
            .y((d) => ctx.yScale(d.y));

        const writeLine = (points: Array<{ x: number; y: number }>): void => {
            this._dataG
                .append('path')
                .datum(points)
                .attr('stroke-opacity', 0)
                .transition()
                .delay(this._duration * 0.75)
                .duration(this._duration * 0.25)
                .attr('stroke-opacity', 1)
                .attr('stroke-dasharray', '10, 5')
                .attr('d', xyLine)
                .attr('fill', 'none')
                .attr('stroke', '#4885ed')
                .attr('stroke-width', 2);
        };

        const writeLineLabel = (
            point: { x: number; y: number },
            text: string,
        ): void => {
            const rectHeight = 20;
            const textOffsetX = 5;

            const g = this._dataG.append('g');
            const r = g
                .append('rect')
                .attr('display', 'block')
                .attr('x', 0)
                .attr('y', -(rectHeight / 2))
                .attr('height', rectHeight)
                .attr('fill', '#808080')
                .attr('rx', 5);
            const t = g
                .append('text')
                .attr('dy', '0.32em')
                .attr('x', textOffsetX)
                .attr('y', 0)
                .attr('fill', '#fff')
                .text(text);
            const width =
                t.node().getBoundingClientRect().width + textOffsetX * 2;
            r.attr('width', width);
            g.attr(
                'transform',
                `translate(${ctx.xScale(point.x) - width / 2}, ${ctx.yScale(
                    point.y,
                )})`,
            );
        };

        if (
            this.data.behaviourTarget != null &&
            !isNaN(this.data.behaviourTarget)
        ) {
            const domain = ctx.yScale.domain();

            writeLine([
                {
                    x: this.data.behaviourTarget,
                    y: domain[0],
                },
                {
                    x: this.data.behaviourTarget,
                    y: domain[1],
                },
            ]);

            writeLineLabel(
                {
                    x: this.data.behaviourTarget,
                    y: (domain[1] - domain[0]) / 2,
                },
                this.data.behaviourTargetText,
            );
        }

        if (
            this.data.outcomeTarget != null &&
            !isNaN(this.data.outcomeTarget)
        ) {
            const domain = ctx.xScale.domain();

            writeLine([
                {
                    x: domain[0],
                    y: this.data.outcomeTarget,
                },
                {
                    x: domain[1],
                    y: this.data.outcomeTarget,
                },
            ]);

            writeLineLabel(
                { x: (domain[1] - domain[0]) / 2, y: this.data.outcomeTarget },
                this.data.outcomeTargetText,
            );
        }
    }
}
